import { useVirtualizer } from '@tanstack/react-virtual';
import { first, isNil, last, sum } from 'lodash';
import { ReactNode, UIEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Text } from '@shared/components/Text';
import { View } from '@shared/components/View';
import { CollectionViewAnalytics } from '@shared/scenes/cards/collectionView/CollectionViewAnalytics';
import { CellField } from '@shared/scenes/cards/tableView/components/Cell';
import { getComponentLabel, isNumberComponent } from '@shared/scenes/workflows/utils';
import { API } from '@shared/types/api';
import { getSubtableColumnWidths, measureTextOnCanvas } from './subtableColumnWidths';
import { getSubtableCellField, getSubtableRowNumber } from './subtableColumns';
import { getRowPressProps } from './subtableRowPress';
import { useRatchetedRowMeasure } from './useRatchetedRowMeasure';
import { SubtableRowRefs } from './useSubtableRows';
import styles from './SubtableTable.module.css';

const ESTIMATED_ROW_HEIGHT = 48;
/** The sticky header row's height (4.25Q in SubtableTable.module.css); rows scroll beneath it. */
const HEADER_HEIGHT = 34;
const ROW_OVERSCAN = 8;
const COLUMN_OVERSCAN = 2;
/** Start fetching the next page when the last rendered row is this close to the end. */
const LOAD_MORE_THRESHOLD = 5;
/** The row-number column ahead of the first column; pinned with it. */
const ROW_NUMBER_WIDTH = 40;

interface Props {
  workflowTemplateId: string;
  columns: API.AnyComponent[];
  rows: API.WorkflowEntityFragment[];
  refs?: SubtableRowRefs;
  hasMore: boolean;
  onLoadMore: () => void;
  /** Set by the SubtableTableModal to modify the table's styling for the modal. */
  modal?: boolean;
  /** The row open in the row menu, highlighted. */
  selectedRowId?: string;
  onRowPress: (entityId: string, index: number) => void;
}

/**
 * Desktop: the subtable's rows as a table. Columns are content-sized (see
 * subtableColumnWidths) and rows and columns are both virtualized inside the field's own
 * scroller, which is capped at about ten rows tall, with the header row pinned, and the row numbers
 * and first column pinned together.
 */
export function SubtableTable({
  workflowTemplateId,
  columns,
  rows,
  refs,
  hasMore,
  onLoadMore,
  modal = false,
  selectedRowId,
  onRowPress,
}: Props) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  // Drives the pinned column's edge shadow (data-scrolled in the CSS). Set only when it flips.
  const [isScrolledRight, setIsScrolledRight] = useState(false);
  const onScroll = (event: UIEvent<HTMLDivElement>) => {
    setIsScrolledRight(event.currentTarget.scrollLeft > 0);
  };

  // CellField only reports analytics for Input Button columns, which a subtable doesn't render.
  const analytics = useMemo(
    () => new CollectionViewAnalytics(workflowTemplateId, ''),
    [workflowTemplateId],
  );

  // The column virtualizer needs every width before rendering, and with rows virtualized most cells
  // aren't in the DOM to measure, so widths come from the loaded rows' data instead. Sizing from
  // rendered cells would widen columns (and shift everything right of them) as rows scroll in.
  const widths = useMemo(
    () => getSubtableColumnWidths(columns, rows, refs, measureTextOnCanvas),
    [columns, rows, refs],
  );
  const [pinnedColumn, ...scrollColumns] = columns;
  const [pinnedWidth = 0, ...scrollWidths] = widths;
  // The row numbers and the first column, pinned together ahead of the virtualized columns.
  const leadingWidth = ROW_NUMBER_WIDTH + pinnedWidth;
  const tableWidth = leadingWidth + sum(scrollWidths);

  const columnVirtualizer = useVirtualizer({
    horizontal: true,
    count: scrollColumns.length,
    getScrollElement: () => scrollerRef.current,
    estimateSize: index => scrollWidths[index] ?? 0,
    // The pinned columns sit at the start of the scroller, ahead of the virtualized columns.
    paddingStart: leadingWidth,
    overscan: COLUMN_OVERSCAN,
  });
  useEffect(() => {
    columnVirtualizer.measure();
  }, [columnVirtualizer, widths]);

  // Row heights only grow: a tall cell scrolled out of view horizontally mustn't shrink its row.
  // New widths mean edited data, which can make rows genuinely shorter, so they reset the heights.
  const measureRow = useRatchetedRowMeasure(widths);
  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollerRef.current,
    estimateSize: () => ESTIMATED_ROW_HEIGHT,
    measureElement: measureRow,
    overscan: ROW_OVERSCAN,
    scrollMargin: HEADER_HEIGHT,
  });

  const virtualColumns = columnVirtualizer.getVirtualItems();
  const columnPaddingLeft = Math.max(0, (first(virtualColumns)?.start ?? 0) - leadingWidth);
  const columnPaddingRight = Math.max(
    0,
    columnVirtualizer.getTotalSize() - (last(virtualColumns)?.end ?? leadingWidth),
  );

  const virtualRows = rowVirtualizer.getVirtualItems();
  const lastRenderedIndex = last(virtualRows)?.index ?? -1;
  useEffect(() => {
    if (hasMore && lastRenderedIndex >= rows.length - LOAD_MORE_THRESHOLD) {
      onLoadMore();
    }
  }, [hasMore, lastRenderedIndex, rows.length, onLoadMore]);

  if (isNil(pinnedColumn)) {
    return null;
  }

  // Columns are virtualized, so each cell says which column it is (aria-colindex, 1-based: the
  // row number is 1, the pinned column 2); the virtualizer's spacers are hidden from assistive tech.
  const renderCells = (
    cellRole: 'columnheader' | 'cell',
    rowNumber: ReactNode,
    renderCell: (column: API.AnyComponent) => JSX.Element,
  ) => (
    <>
      <View
        role={cellRole}
        aria-colindex={1}
        className={[styles.cell, styles.rowNumber]}
        style={{ width: ROW_NUMBER_WIDTH }}
      >
        {rowNumber}
      </View>
      <View
        role={cellRole}
        aria-colindex={2}
        className={[styles.cell, styles.pinned]}
        style={{ width: pinnedWidth, left: ROW_NUMBER_WIDTH }}
      >
        {renderCell(pinnedColumn)}
      </View>
      {columnPaddingLeft > 0 && <View aria-hidden style={{ width: columnPaddingLeft }} />}
      {virtualColumns.map(virtualColumn => {
        const column = scrollColumns[virtualColumn.index];
        return (
          <View
            key={column.id}
            role={cellRole}
            aria-colindex={virtualColumn.index + 3}
            className={[styles.cell, isNumberComponent(column) && styles.numeric]}
            style={{ width: scrollWidths[virtualColumn.index] }}
          >
            {renderCell(column)}
          </View>
        );
      })}
      {columnPaddingRight > 0 && <View aria-hidden style={{ width: columnPaddingRight }} />}
    </>
  );

  return (
    <View
      ref={scrollerRef}
      className={[styles.scroller, modal && styles.modal]}
      data-scrolled={isScrolledRight || undefined}
      onScroll={onScroll}
    >
      {/* Rows are virtualized too: the counts and each row's aria-rowindex (the header is row 1)
          let screen readers announce real positions, not just the rendered slice. */}
      <View
        role="table"
        aria-rowcount={rows.length + 1}
        aria-colcount={columns.length + 1}
        className={styles.table}
        style={{ width: tableWidth }}
      >
        <View row role="row" aria-rowindex={1} className={styles.headerRow}>
          {/* The row-number column has no header. */}
          {renderCells('columnheader', null, column => (
            <Text
              size="overline"
              weight="semibold"
              inactive
              numberOfLines={1}
              className={styles.headerLabel}
            >
              {getComponentLabel(column)}
            </Text>
          ))}
        </View>
        <View
          role="rowgroup"
          className={styles.body}
          style={{ height: rowVirtualizer.getTotalSize() }}
        >
          {virtualRows.map(virtualRow => {
            const row = rows[virtualRow.index];
            return (
              <View
                key={row.id}
                ref={rowVirtualizer.measureElement}
                data-index={virtualRow.index}
                data-row-id={row.id}
                row
                role="row"
                aria-rowindex={virtualRow.index + 2}
                {...getRowPressProps(() => onRowPress(row.id, virtualRow.index))}
                className={[styles.row, row.id === selectedRowId && styles.selected]}
                style={{ transform: `translateY(${virtualRow.start - HEADER_HEIGHT}px)` }}
              >
                {renderCells(
                  'cell',
                  <Text size="body2" inactive>
                    {getSubtableRowNumber(virtualRow.index)}
                  </Text>,
                  column => (
                    <CellField
                      field={getSubtableCellField(row, column, refs)}
                      component={column}
                      workflowTemplateId={workflowTemplateId}
                      entityId={row.id}
                      canUpdateCard={false}
                      analytics={analytics}
                    />
                  ),
                )}
              </View>
            );
          })}
        </View>
      </View>
    </View>
  );
}
